// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/app/stores/auth-store';
import { useCreditStore } from '@/app/stores/credit-store';
import {
  VAULT_STATUS_KEY,
  SYNC_STATUS_KEY,
} from '@/app/features/storage/hooks/useVault';
import type { components } from '@flaircv/api-client';
import { toLoginOutcome, type LoginOutcome } from '../types/login-outcome';

type UserSummary = components['schemas']['UserSummary'];
type AuthResponse = components['schemas']['AuthResponse'];

/** Maps an API UserSummary to the auth store's User shape. */
function mapUser(apiUser: UserSummary) {
  return {
    id: apiUser.id,
    email: apiUser.email,
    creditBalance: apiUser.credit_balance,
    aiProvider: apiUser.ai_provider,
    storageProvider: apiUser.storage_provider,
    hasPassword: apiUser.has_password,
    emailVerified: apiUser.email_verified,
  };
}

/**
 * Best-effort vault unlock after login/register.
 * Derives a 32-byte AES-256 key via Argon2id(password, userUUID),
 * then sends it to the backend to store in Redis.
 *
 * Non-blocking: login succeeds regardless. If this fails, the sync
 * status indicator will show "red" and prompt the user later.
 *
 * The vault-status and sync-status queries MUST be invalidated once the unlock
 * lands. Both are `staleTime: Infinity` — deliberately, so they only ever
 * refresh on an explicit invalidation — and this unlock changes what both of
 * them return. Without the invalidation the app keeps serving whatever was
 * cached from before the unlock, which for a purged user means `data_hydrated`
 * reads stale: the "Restoring your data..." overlay never appears, and the user
 * is dropped into an app that looks empty and 403s every save, with nothing
 * on screen explaining why or offering a way out short of a page reload.
 *
 * Exported for tests: the invalidation is a side effect with no visible trace
 * at this layer, so it is only assertable by calling this directly.
 */
export async function unlockVaultAfterAuth(
  password: string,
  userID: string,
  accessToken: string,
  queryClient: QueryClient,
): Promise<void> {
  try {
    const { deriveVaultKey, bytesToBase64 } = await import('@/lib/crypto');
    const keyBytes = await deriveVaultKey(password, userID);
    const keyBase64 = bytesToBase64(keyBytes);

    const response = await fetch('/api/v1/vault/unlock', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ decryption_key: keyBase64 }),
    });

    // fetch only rejects on network failure, so a 4xx/5xx arrives here as a
    // perfectly ordinary response. Without this check a rejected unlock was
    // indistinguishable from a successful one.
    if (!response.ok) {
      throw new Error(`vault unlock failed with ${response.status}`);
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: VAULT_STATUS_KEY }),
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY }),
    ]);
  } catch {
    // Vault unlock is best-effort. The sync status indicator
    // will surface the locked state to the user if this fails.
    console.warn('[vault] Failed to unlock vault after login');
  }
}

/**
 * Everything that turns an AuthResponse into a signed-in app: store the
 * tokens and user, seed the credit balance, kick off the best-effort vault
 * unlock while the password is still in scope, and route to the right page.
 *
 * Shared by registration, password login, and the emailed-code step, so the
 * three cannot drift apart.
 */
export function useCompleteSignIn() {
  const { setTokens, setUser } = useAuthStore();
  const { setBalance } = useCreditStore();
  const router = useRouter();
  const queryClient = useQueryClient();

  return useCallback(
    (data: AuthResponse, password: string) => {
      setTokens(data.data.access_token, data.data.refresh_token);
      setUser(mapUser(data.data.user));
      setBalance(data.data.user.credit_balance);

      // Fire-and-forget vault unlock with the password still in scope.
      unlockVaultAfterAuth(
        password,
        data.data.user.id,
        data.data.access_token,
        queryClient,
      );

      // Unverified users must verify their email first.
      const emailVerified = (data.data.user as Record<string, unknown>).email_verified;
      if (emailVerified === false) {
        router.navigate({ to: '/auth/verify-email' as string as '/' });
      } else {
        router.navigate({ to: '/dashboard' });
      }
    },
    [queryClient, router, setBalance, setTokens, setUser],
  );
}

/** Hook for user registration. Stores tokens and redirects to dashboard. */
export function useRegister() {
  const completeSignIn = useCompleteSignIn();

  return useMutation({
    mutationFn: async (params: { email: string; password: string; captchaToken?: string }) => {
      const { data, error } = await apiClient.POST('/api/v1/auth/register', {
        body: { email: params.email, password: params.password, captcha_token: params.captchaToken },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return { data, password: params.password };
    },
    onSuccess: ({ data, password }) => completeSignIn(data, password),
  });
}

/**
 * Hook for user login.
 *
 * A login has two possible outcomes. When the server issues a session the
 * user is signed in on the spot. When it answers 202 with a challenge, the
 * password was right but the user still has to enter the code that was just
 * emailed to them; the mutation result carries what the code step needs and
 * nothing is stored until that step succeeds.
 */
export function useLogin() {
  const completeSignIn = useCompleteSignIn();

  return useMutation({
    mutationFn: async (params: {
      email: string;
      password: string;
      captchaToken?: string;
    }): Promise<LoginOutcome> => {
      const { data, error } = await apiClient.POST('/api/v1/auth/login', {
        body: { email: params.email, password: params.password, captcha_token: params.captchaToken },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return toLoginOutcome(data, params.email, params.password);
    },
    onSuccess: (outcome) => {
      if (outcome.kind === 'session') {
        completeSignIn(outcome.data, outcome.password);
      }
    },
  });
}

/** Hook for user logout. Calls API, clears store, redirects to login. */
export function useLogout() {
  const { accessToken, refreshToken, logout } = useAuthStore();
  const router = useRouter();

  return useMutation({
    mutationFn: async () => {
      if (!refreshToken) return;

      // Best-effort server call. Even if it fails, we clear local state.
      await apiClient.POST('/api/v1/auth/logout', {
        body: { refresh_token: refreshToken },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    },
    onSettled: () => {
      // Always clear local state, even if server call failed.
      logout();
      router.navigate({ to: '/auth/login' });
    },
  });
}

/**
 * Attempts to refresh the access token using the stored refresh token.
 * Returns the new tokens on success, or null on failure.
 * This is called by the auth middleware, not directly by components.
 */
export async function refreshAccessToken(): Promise<{
  accessToken: string;
  refreshToken: string;
} | null> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return null;

  const { data, error } = await apiClient.POST('/api/v1/auth/refresh', {
    body: { refresh_token: refreshToken },
  });

  if (error) {
    // Refresh failed — force logout.
    useAuthStore.getState().logout();
    return null;
  }

  const newTokens = {
    accessToken: data.data.access_token,
    refreshToken: data.data.refresh_token,
  };

  useAuthStore.getState().setTokens(newTokens.accessToken, newTokens.refreshToken);
  return newTokens;
}
